<template>
  <v-row class="flex-column" align="center" justify="center">
    <!--VIDEO STREAM-->
    <v-col cols="12" class="d-flex justify-center">
      <video
        id="video"
        ref="video"
        autoplay
        muted
        playsinline
        :class="{ redBorder: recording, border: !recording }"
        class="video-preview"
      />
    </v-col>
    <!--BUTTONS-->
    <v-col cols="12">
      <v-row class="justify-center text-center ga-3">
        <v-btn
          v-if="canSwitchCamera"
          :disabled="disabled"
          color="#747474"
          variant="outlined"
          icon
          @click="switchCamera"
        >
          <v-icon color="#747474"> mdi-camera-switch </v-icon>
        </v-btn>
        <v-btn
          v-if="!recording"
          :disabled="disabled"
          color="#747474"
          variant="outlined"
          icon
          @click="startRecording"
        >
          <v-icon color="#747474" size="x-large"> mdi-camera </v-icon>
        </v-btn>
        <v-btn
          v-else
          :disabled="disabled"
          color="#747474"
          variant="outlined"
          icon
          @click="stopRecording"
        >
          <v-icon color="#747474" size="x-large"> mdi-camera </v-icon>
        </v-btn>
      </v-row>
    </v-col>
    <!--TIMER-->
    <v-col cols="12" class="text-center">
      <div v-if="recording">
        {{ timerValue | timerFilter }}
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { useDisplay } from "vuetify";
let chunks = [];

export default {
  filters: {
    timerFilter(value) {
      const minutes = Math.floor(value / 60);
      const seconds = value - minutes * 60;
      const min = minutes.toString().length > 1 ? `${minutes}` : `0${minutes}`;
      const sec = seconds.toString().length > 1 ? `${seconds}` : `0${seconds}`;
      return `${min}:${sec}`;
    },
  },
  data: () => ({
    front: false,
    recording: false,
    disabled: false,
    timerValue: 0,
    timerInverval: null,
    stream: null,
    recorder: null,
    videoInputsCount: 0,
  }),
  computed: {
    display() {
      return useDisplay();
    },
    videoWidth() {
      // Vuetify 3 breakpoints are reactive booleans
      if (this.$vuetify.display.xs) return 240;
      if (this.$vuetify.display.sm) return 480;
      if (this.$vuetify.display.md) return 680;
      // lg and xl fallback
      return 680;
    },
    canSwitchCamera() {
      return this.videoInputsCount > 1;
    },
  },
  watch: {
    timerValue(value) {
      if (value >= 30) this.stopRecording(); // Limit recording time to 30s.
    },
  },
  mounted() {
    this.startCamera();
  },
  methods: {
    async refreshVideoInputsCount() {
      if (!navigator?.mediaDevices?.enumerateDevices) {
        this.videoInputsCount = 0;
        return;
      }
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        this.videoInputsCount = devices.filter((d) => d.kind === "videoinput")
          .length;
      } catch {
        this.videoInputsCount = 0;
      }
    },
    async startCamera() {
      const el = this.$refs.video;
      if (!el || !navigator?.mediaDevices?.getUserMedia) return;

      // Always stop previous stream first (important on mobile).
      this.stopCamera({ disableUi: false });

      const constraints = {
        video: { facingMode: this.front ? "user" : "environment" },
        audio: true,
      };

      try {
        this.disabled = false;
        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
        await this.refreshVideoInputsCount();

        // Ensure iOS/Safari plays the preview inline.
        el.muted = true;
        el.playsInline = true;
        el.autoplay = true;
        el.srcObject = this.stream;

        // `play()` can reject without a gesture; ignore and let user tap record.
        const p = el.play?.();
        if (p?.catch) p.catch(() => {});
      } catch (err) {
        // keep silent in UI; caller can decide how to message
        // eslint-disable-next-line no-console
        console.log(err);
      }
    },
    stopCamera({ disableUi } = { disableUi: true }) {
      if (disableUi) this.disabled = true;
      const el = this.$refs.video;
      if (this.stream) {
        this.stream.getTracks().forEach((t) => t.stop());
        this.stream = null;
      }
      if (el?.srcObject) el.srcObject = null;
    },
    switchCamera() {
      this.front = !this.front;
      this.startCamera();
    },
    startRecording() {
      this.recording = true;
      this.timerInverval = setInterval(() => {
        this.timerValue += 1;
      }, 1000);
      if (!this.stream) return;
      this.recorder = new MediaRecorder(this.stream);
      this.recorder.ondataavailable = (ev) => {
        chunks.push(ev.data);
      };
      this.recorder.start();
    },
    stopRecording() {
      this.recording = false;
      clearInterval(this.timerInverval);
      this.timerInverval = null;
      this.timerValue = 0;
      if (!this.recorder) return;
      this.recorder.stop();
      this.recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/mp4" });
        this.$emit("video", { blob });
        chunks = [];
      };
    },
  },
};
</script>

<style scoped>
.video-preview {
  display: block;
  width: 100%;
  max-width: 680px;
  height: auto;
}

@media (max-width: 600px) {
  .video-preview {
    max-width: 100%;
  }
}

.border {
  display: block;
  border-style: solid;
  border-width: 2px;
  border-radius: 5px;
  color: #455a64;
  /*color: #616161;*/
}
.redBorder {
  display: block;
  border-style: solid;
  border-width: 2px;
  border-radius: 5px;
  color: #d32f2f;
}
</style>
